// src/components/NewInvoice/LooseStoneItemForm.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { FaTrash } from "react-icons/fa";
import { useSelector } from "react-redux";
import Select from "react-select";
import { Controller, useWatch } from "react-hook-form";
import { handleDropDowns } from "../../config/dropDownHelpers";

// toast
const toast = (msg, type = "error") => {
  if (window?.toastr?.[type]) return window.toastr[type](msg);
  if (window?.toast?.[type]) return window.toast[type](msg);
  if (window?.notify) return window.notify(msg, { type });
  alert(msg);
};

const baseSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#A78BFA" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(167,139,250,0.35)" : "none",
    fontSize: "12px",
    backgroundColor: "white",
  }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
  clearIndicator: (base) => ({ ...base, padding: "4px" }),
  valueContainer: (base) => ({ ...base, padding: "0 6px", height: "30px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menu: (base) => ({ ...base, fontSize: "12px", zIndex: 40 }),
  menuList: (base) => ({ ...base, maxHeight: "100px", overflowY: "auto" }),
  singleValue: (base) => ({ ...base, fontSize: "12px" }),
};
const withErrorBorder = (stylesObj, hasError) =>
  !hasError
    ? stylesObj
    : {
        ...stylesObj,
        control: (base, state) => {
          const baseCtrl = stylesObj.control
            ? stylesObj.control(base, state)
            : base;
          return {
            ...baseCtrl,
            borderColor: "#ef4444",
            boxShadow: state.isFocused
              ? "0 0 0 2px rgba(239,68,68,0.2)"
              : "none",
            "&:hover": { borderColor: "#ef4444" },
          };
        },
      };

const inputCls = (hasError) =>
  `w-full px-2 py-[6px] text-sm border rounded ${
    hasError
      ? "border-red-400 focus:outline-none focus:ring-2 focus:ring-red-300"
      : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-300"
  }`;

const LooseStoneItemForm = ({
  control,
  register,
  errors,
  rowindex,
  fieldId,
  onDelete,
  watch,
  setValue,
  getValues,
  showDelete = true,
  cardHeightClass = "h-[40vh] sm:h-[40vh]",
  registerFirstField,
  selectedBranch,
}) => {
  const formRef = useRef(null);
  const stonesData = useSelector((s) => s.stones?.stones) || [];
  const countersFromState = useSelector((s) => s.counters?.counters) || [];

  const path = (f) => `looseStones.${rowindex}.${f}`;
  const getNum = (f) => Number(getValues(path(f)) || 0);

  // per-row counter
  const hasNameErr = !!errors?.looseStones?.[rowindex]?.stnPopupName;
  const hasCounterErr = !!errors?.looseStones?.[rowindex]?.counterRow;

  const stoneOptions = useMemo(
    () =>
      (stonesData || []).map((s) => ({
        value: String(s?._id),
        label: [
          s?.stoneName ?? "",
          s?.unit ? `(${s.unit})` : "",
          s?.shapeName ? `• ${s.shapeName}` : "",
          s?.sizeName ? `• ${s.sizeName}` : "",
          s?.colorName ? `• ${s.colorName}` : "",
        ]
          .filter(Boolean)
          .join(" "),
        info: s,
      })),
    [stonesData]
  );

  const recalc = () => {
    const wt = getNum("stnPopupWt");
    const rWt = getNum("stnPopupRateByWt");
    const pcs = getNum("stnPopupPcs");
    const rPc = getNum("stnPopupRateByPcs");
    const gstRate = Math.max(0, getNum("gstRate"));
    const mrp = getNum("mrp");

    let amt = 0;
    if (wt > 0 && rWt > 0 && pcs === 0) {
      amt = +(wt * rWt).toFixed(2);
    } else if (pcs > 0 && rPc > 0) {
      amt = +(pcs * rPc).toFixed(2);
    }

    setValue(path("itemAmount"), +amt.toFixed(2));

    if (mrp > 0) {
      setValue(path("itemGstAmount"), 0);
      setValue(path("totalItemAmount"), +mrp.toFixed(2));
    } else {
      const gstAmt = +((amt * gstRate) / 100).toFixed(2);
      setValue(path("itemGstAmount"), gstAmt);
      setValue(path("totalItemAmount"), +(amt + gstAmt).toFixed(2));
    }
  };

  const applyManualFinal = (target) => {
    const mrp = getNum("mrp");
    if (mrp > 0) {
      toast("MRP item total is locked (tax already included).", "error");
      setValue(path("totalItemAmount"), +mrp.toFixed(2));
      return;
    }
    const autoAmt = getNum("itemAmount");
    const autoGst = getNum("itemGstAmount");
    const autoTotal = +(autoAmt + autoGst).toFixed(2);

    if (!Number.isFinite(target) || target <= 0) {
      setValue(path("totalItemAmount"), autoTotal);
      return;
    }
    if (target < autoTotal - 0.001) {
      toast(
        "Final amount cannot be reduced here. Use invoice discount.",
        "error"
      );
      setValue(path("totalItemAmount"), autoTotal);
      return;
    }
    if (target <= autoTotal + 0.001)
      setValue(path("totalItemAmount"), autoTotal);
    else {
      const rate = Math.max(0, getNum("gstRate"));
      const deltaIncl = +(target - autoTotal).toFixed(2);
      const addPretax = +(deltaIncl / (1 + rate / 100)).toFixed(2);
      const newPretax = +(autoAmt + addPretax).toFixed(2);
      const newGst = +((newPretax * rate) / 100).toFixed(2);
      setValue(path("itemAmount"), newPretax);
      setValue(path("itemGstAmount"), newGst);
      setValue(path("totalItemAmount"), +(newPretax + newGst).toFixed(2));
    }
  };

  // Ctrl+Delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showDelete) return;
      if (e.ctrlKey && e.key === "Delete") {
        if (
          formRef.current &&
          formRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          onDelete?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDelete, showDelete]);

  return (
    <motion.div
      ref={formRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: rowindex * 0.05 }}
      id={`loose-card-${fieldId}`}
      data-field-id={fieldId}
      className={`invoice-card ${cardHeightClass} snap-start px-2 pt-1 rounded-lg border border-gray-200 hover:border-gray-300 shadow-lg bg-white relative overflow-y-auto`}
    >
      <div className="mb-0.5 flex items-center justify-between sticky top-0 bg-white pt-1 pb-1">
        <h3 className="text-sm font-semibold text-gray-900">
          #{rowindex + 1} • Loose Stone
        </h3>
        <div className="flex-1 h-px bg-gray-200 mx-2" />
        {showDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-gray-500 hover:text-red-600 transition-all duration-200 hover:scale-110"
            title="Delete this item"
          >
            <FaTrash className="text-[14px]" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-12 gap-x-2 gap-y-1">
        {/* NEW: per-row Counter */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Counter <span className="text-red-500">*</span>
          </label>
          <Controller
            name={path("counterRow")}
            control={control}
            rules={{ required: "Counter is required" }}
            render={({ field }) => (
              <Select
                {...field}
                options={handleDropDowns({
                  key: "counter",
                  counters: countersFromState,
                  branch: selectedBranch,
                })}
                classNamePrefix="react-select"
                styles={withErrorBorder(baseSelectStyles, hasCounterErr)}
                placeholder="Select Counter"
                isClearable
                isSearchable
                onChange={(opt) => {
                  field.onChange(opt ?? null);
                  // snapshot primitives
                  setValue(path("counterId"), opt?.value || "");
                  setValue(path("counterName"), opt?.label || "");
                  setValue(path("branchId"), opt?.info?.branchId || "");
                  setValue(path("branchName"), opt?.info?.branchName || "");
                }}
                ref={(selectRef) => {
                  if (registerFirstField && selectRef) {
                    registerFirstField(fieldId, {
                      focus: () => selectRef?.focus?.(),
                    });
                  }
                }}
              />
            )}
          />
        </div>

        {/* Name */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Name
          </label>
          <Controller
            name={path("stnPopupName")}
            control={control}
            rules={{ required: "Stone name is required" }}
            render={({ field }) => (
              <Select
                {...field}
                options={stoneOptions}
                classNamePrefix="react-select"
                styles={withErrorBorder(baseSelectStyles, hasNameErr)}
                placeholder="-- Select --"
                isClearable
                isSearchable
                menuPlacement="bottom"
                onChange={(opt) => {
                  field.onChange(opt);
                  const sel = (stonesData || []).find(
                    (s) => String(s?._id) === String(opt?.value)
                  );
                  if (!sel) return;
                  setValue(path("unit"), sel?.unit || null);
                  setValue(path("shape"), sel?.shape?._id || "");
                  setValue(path("size"), sel?.size?._id || "");
                  setValue(path("color"), sel?.color?._id || "");
                  setValue(path("settingType"), sel?.settingType?._id || "");
                  setValue(path("stnPopupWt"), sel?.stnPopupWt ?? "");
                  setValue(
                    path("stnPopupRateByWt"),
                    sel?.stnPopupRateByWt ?? ""
                  );
                  setValue(path("stnPopupPcs"), sel?.stnPopupPcs ?? "");
                  setValue(
                    path("stnPopupRateByPcs"),
                    sel?.stnPopupRateByPcs ?? ""
                  );
                  setValue(
                    path("stnPopupDescription"),
                    sel?.stnPopupDescription ?? ""
                  );
                  if (typeof sel?.gstRate === "number")
                    setValue(path("gstRate"), sel.gstRate);
                  recalc();
                }}
                filterOption={(candidate, raw) => {
                  const q = (raw || "").toLowerCase();
                  const label = (candidate?.label || "")
                    .toString()
                    .toLowerCase();
                  return label.includes(q);
                }}
              />
            )}
          />
        </div>

        {/* Weight */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            Weight
          </label>
          <input
            type="text"
            {...register(path("stnPopupWt"), { onChange: recalc })}
            className={inputCls()}
            placeholder="Weight"
          />
        </div>

        {/* Rate/Wt */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            Rate / Wt
          </label>
          <input
            type="text"
            {...register(path("stnPopupRateByWt"), { onChange: recalc })}
            className={inputCls()}
            placeholder="Rate/Wt"
          />
        </div>

        {/* Pcs */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            Pieces
          </label>
          <input
            type="text"
            {...register(path("stnPopupPcs"), { onChange: recalc })}
            className={inputCls()}
            placeholder="Pcs"
          />
        </div>

        {/* Rate/Pcs */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            Rate / Pcs
          </label>
          <input
            type="text"
            {...register(path("stnPopupRateByPcs"), { onChange: recalc })}
            className={inputCls()}
            placeholder="Rate/Pcs"
          />
        </div>

        {/* Amount (pre-tax) */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            Item Amount
          </label>
          <input
            type="text"
            {...register(path("itemAmount"))}
            disabled
            className="w-full px-2 py-[6px] text-sm border rounded bg-gray-50"
            placeholder="0.00"
          />
        </div>

        {/* GST % */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            GST %
          </label>
          <input
            type="text"
            {...register(path("gstRate"), { onChange: recalc })}
            className={inputCls()}
            placeholder="e.g. 3"
          />
        </div>

        {/* GST Amt */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">Tax</label>
          <input
            type="text"
            {...register(path("itemGstAmount"))}
            disabled
            className="w-full px-2 py-[6px] text-sm border rounded bg-gray-50"
            placeholder="0.00"
          />
        </div>

        {/* MRP */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-700">
            MRP (incl. tax)
          </label>
          <input
            type="text"
            {...register(path("mrp"), { onChange: recalc })}
            className={inputCls()}
            placeholder="0.00"
          />
        </div>

        {/* Final Amount */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700">
            Final Amount
          </label>
          <input
            type="text"
            {...register(path("totalItemAmount"), {
              onBlur: (e) => applyManualFinal(parseFloat(e.target.value)),
            })}
            className={inputCls()}
            placeholder="0.00"
          />
        </div>

        {/* Description */}
        <div className="md:col-span-4">
          <label className="block text-xs font-medium text-gray-700">
            Description
          </label>
          <input
            type="text"
            {...register(path("stnPopupDescription"))}
            className={inputCls()}
            placeholder="Optional"
          />
        </div>
      </div>
    </motion.div>
  );
};

export default LooseStoneItemForm;
